import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toLocalIsoString } from "@/lib/agendaConstants";
import { INVENTORY } from "@/constants/testIds";

const EMPTY_FORM = { product_id: "", quantity: "", unit_cost: "", supplier: "", purchase_date: toLocalIsoString(new Date()), notes: "" };

/** Registrar un ingreso de mercaderia: suma stock y actualiza el costo del producto. */
export default function PurchaseModal({ open, onOpenChange, products, onSubmit }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError("");
    }
  }, [open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.product_id) { setError("Selecciona un producto"); return; }
    if (!form.quantity || Number(form.quantity) <= 0) { setError("Ingresa una cantidad válida"); return; }
    if (form.unit_cost === "" || Number(form.unit_cost) < 0) { setError("Ingresa el costo unitario de esta compra"); return; }
    setSaving(true);
    setError("");
    try {
      await onSubmit({
        product_id: form.product_id,
        quantity: Number(form.quantity),
        unit_cost: Number(form.unit_cost),
        supplier: form.supplier.trim() || null,
        purchase_date: form.purchase_date,
        notes: form.notes.trim() || null,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "No se pudo registrar la compra");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={INVENTORY.purchaseModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Registrar compra</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Producto</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm((f) => ({ ...f, product_id: v }))}>
              <SelectTrigger data-testid={INVENTORY.purchaseModalProductSelect}><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Cantidad</Label>
              <Input type="number" min="0" step="0.01" value={form.quantity} onChange={set("quantity")} data-testid={INVENTORY.purchaseModalQuantityInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Costo unitario (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.unit_cost} onChange={set("unit_cost")} data-testid={INVENTORY.purchaseModalCostInput} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Proveedor (opcional)</Label>
            <Input value={form.supplier} onChange={set("supplier")} data-testid={INVENTORY.purchaseModalSupplierInput} placeholder="Ej: Distribuidora Allergan" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fecha de compra</Label>
            <Input type="datetime-local" value={form.purchase_date} onChange={set("purchase_date")} data-testid={INVENTORY.purchaseModalDateInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Notas</Label>
            <Textarea value={form.notes} onChange={set("notes")} data-testid={INVENTORY.purchaseModalNotesInput} rows={2} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={INVENTORY.purchaseModalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={INVENTORY.purchaseModalSaveButton}>
            {saving ? "Guardando..." : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
